import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import PanelRouter, { PanelRoute } from './components/PanelRouter';
import TenantRouter from './components/TenantRouter';
import HomePage         from './pages/HomePage';
import LoginPage        from './pages/LoginPage';
import RSMinCallbackPage from './pages/RSMinCallbackPage';
import RegisterPage     from './pages/RegisterPage';
import DashboardPage    from './pages/DashboardPage';
import ProjectPage      from './pages/ProjectPage';
import QuestionnairePage from './pages/QuestionnairePage';
import MembershipPage   from './pages/MembershipPage';
import AdminPage        from './pages/AdminPage';
import ReportsPage      from './pages/ReportsPage';
import ReportDetailPage from './pages/ReportDetailPage';
import AssessmentsPage  from './pages/AssessmentsPage';
import PipelinePage     from './pages/PipelinePage';
import CommunityAdminPage from './pages/CommunityAdminPage';
import ConsultationPage  from './pages/ConsultationPage';
import CommunityNewsPage from './pages/CommunityNewsPage';
import CommunityDataPage from './pages/CommunityDataPage';
import HarvestingPage    from './pages/HarvestingPage';
import ComsPage          from './pages/ComsPage';
import AdminConnectorPage   from './pages/AdminConnectorPage';
import LensesPage           from './pages/LensesPage';
import SevenGenPage         from './pages/SevenGenPage';
import LandValuesPage       from './pages/LandValuesPage';
import LandIntelligencePage from './pages/LandIntelligencePage';
import LandUnitsPage        from './pages/LandUnitsPage';
import TenantDashboardPage       from './pages/TenantDashboardPage';
import TenantRegistryPage        from './pages/TenantRegistryPage';
import TenantJoinPage            from './pages/TenantJoinPage';
import ProponentPage             from './pages/ProponentPage';
import ProjectFormPage           from './pages/ProjectFormPage';
import ProponentProfilePage      from './pages/ProponentProfilePage';
import AggregateSpeciesMapPage   from './pages/AggregateSpeciesMapPage';
import AggregatePollutionMapPage from './pages/AggregatePollutionMapPage';
import AggregateHarvestingMapPage from './pages/AggregateHarvestingMapPage';
import CrossCommunityReportPage  from './pages/CrossCommunityReportPage';
import './App.css';

// ─── Module-by-panel placement map ────────────────────────────────────────────
//
//  Panel       │  Modules / routes available
//  ────────────┼──────────────────────────────────────────────────────────────
//  admin       │  dashboard, admin (tenants/users), assessments, reports,
//              │  report-detail, pipeline, projects/:id
//  staff       │  dashboard, assessments, reports, report-detail, pipeline,
//              │  projects/:id
//  community   │  dashboard, membership, projects/:id
//              │  + consultation, communityNews, communityData (if enabled)
//  user        │  dashboard, membership
//              │  + communityNews, communityData (if enabled)
//  genpop      │  dashboard, membership (join/pending)
//
// The PanelRoute guard enforces access. The shell sidenav shows the right links.
// ──────────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* ── Public (no auth required) ── */}
          <Route path="/"          element={<HomePage />} />
          <Route path="/login"     element={<LoginPage />} />
          <Route path="/auth/rsmin-callback" element={<RSMinCallbackPage />} />
          <Route path="/register"  element={<RegisterPage />} />

          {/* ── Public: community self-service onboarding ── */}
          <Route path="/register-community" element={<TenantRegistryPage />} />

          {/* ── Semi-public: questionnaire responses can come from anonymous users ── */}
          <Route path="/questionnaires/:id" element={<QuestionnairePage />} />

          {/* ── Authenticated: PanelRouter guards auth + renders the correct shell ── */}
          <Route element={<PanelRouter />}>

            {/* ── All panels: dashboard + project detail ── */}
            <Route path="/dashboard"     element={<DashboardPage />} />
            <Route path="/projects/:id"  element={<ProjectPage />} />

            {/* ── community | user | genpop: membership ── */}
            <Route path="/membership" element={<MembershipPage />} />

            {/* ── proponent: cross-community view ── */}
            <Route
              path="/proponent/new"
              element={
                <PanelRoute panels={['proponent']}>
                  <ProjectFormPage />
                </PanelRoute>
              }
            />
            <Route
              path="/proponent/profile"
              element={
                <PanelRoute panels={['proponent']}>
                  <ProponentProfilePage />
                </PanelRoute>
              }
            />
            <Route
              path="/proponent"
              element={
                <PanelRoute panels={['proponent']}>
                  <ProponentPage />
                </PanelRoute>
              }
            />

            {/* ── admin only ── */}
            <Route
              path="/admin"
              element={
                <PanelRoute panels={['admin']}>
                  <AdminPage />
                </PanelRoute>
              }
            />

            {/* ── admin | staff ── */}
            <Route
              path="/assessments"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <AssessmentsPage />
                </PanelRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <ReportsPage />
                </PanelRoute>
              }
            />
            <Route
              path="/reports/:id"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <ReportDetailPage />
                </PanelRoute>
              }
            />
            <Route
              path="/pipeline"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <PipelinePage />
                </PanelRoute>
              }
            />

            {/* ── community: admin management (community_admin role) ── */}
            <Route
              path="/community-admin"
              element={
                <PanelRoute panels={['community']} communityRoles={['community_admin']}>
                  <CommunityAdminPage />
                </PanelRoute>
              }
            />

            {/* ── community | user — module-gated community tools ── */}
            <Route
              path="/consultation"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="consultation">
                  <ConsultationPage />
                </PanelRoute>
              }
            />
            <Route
              path="/community-news"
              element={
                <PanelRoute panels={['community', 'user']} module="communityNews">
                  <CommunityNewsPage />
                </PanelRoute>
              }
            />
            <Route
              path="/community-data"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="communityData">
                  <CommunityDataPage />
                </PanelRoute>
              }
            />
            <Route
              path="/harvesting"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="harvesting">
                  <HarvestingPage />
                </PanelRoute>
              }
            />
            <Route
              path="/coms"
              element={
                <PanelRoute panels={['admin', 'staff', 'community']}>
                  <ComsPage />
                </PanelRoute>
              }
            />
            <Route
              path="/admin/connector"
              element={
                <PanelRoute panels={['admin']}>
                  <AdminConnectorPage />
                </PanelRoute>
              }
            />

            {/* ── community | user — land values (module-gated) ── */}
            <Route
              path="/land-values"
              element={
                <PanelRoute panels={['community', 'user']} module="landValues">
                  <LandValuesPage />
                </PanelRoute>
              }
            />
            <Route
              path="/land-units"
              element={
                <PanelRoute panels={['community', 'user', 'admin', 'staff']} module="landUnits">
                  <LandUnitsPage />
                </PanelRoute>
              }
            />

            {/* ── admin | staff — platform intelligence tools ── */}
            <Route
              path="/lenses"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <LensesPage />
                </PanelRoute>
              }
            />
            <Route
              path="/seven-gen"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <SevenGenPage />
                </PanelRoute>
              }
            />
            <Route
              path="/land-intelligence"
              element={
                <PanelRoute panels={['admin', 'staff']}>
                  <LandIntelligencePage />
                </PanelRoute>
              }
            />

          </Route>{/* end PanelRouter */}

          {/* ── Tenant-view mode: /t/:tenantSlug ── */}
          {/* Accessible to platform staff (any slug) and active community members. */}
          {/* TenantRouter owns auth guard + TenantProvider; TenantShell renders chrome. */}

          {/* Public: per-tenant branded join page — must be BEFORE TenantRouter so no auth guard ── */}
          <Route path="/t/:tenantSlug/join" element={<TenantJoinPage />} />

          <Route path="/t/:tenantSlug" element={<TenantRouter />}>
            <Route index                  element={<TenantDashboardPage />} />
            <Route path="dashboard"       element={<TenantDashboardPage />} />
            <Route path="consultation"    element={<ConsultationPage />} />
            <Route path="community-news"  element={<CommunityNewsPage />} />
            <Route path="community-data"  element={<CommunityDataPage />} />
            <Route path="harvesting"      element={<HarvestingPage />} />
            <Route path="land-values"     element={<LandValuesPage />} />
            <Route path="land-units"      element={<LandUnitsPage />} />
            <Route path="community-admin" element={<CommunityAdminPage />} />

            {/* ── Government tenant: aggregate map + report modules ── */}
            <Route
              path="aggregate-species-map"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="aggregateSpeciesMap">
                  <AggregateSpeciesMapPage />
                </PanelRoute>
              }
            />
            <Route
              path="aggregate-pollution-map"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="aggregatePollutionMap">
                  <AggregatePollutionMapPage />
                </PanelRoute>
              }
            />
            <Route
              path="aggregate-harvesting-map"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="aggregateHarvestingMap">
                  <AggregateHarvestingMapPage />
                </PanelRoute>
              }
            />
            <Route
              path="cross-community-report"
              element={
                <PanelRoute panels={['admin', 'staff', 'community', 'user']} module="crossCommunityReport">
                  <CrossCommunityReportPage />
                </PanelRoute>
              }
            />
          </Route>

        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;

